import { env } from '../config/env'
import { api } from './api'

const stripTrailingSlashes = (value: string): string => value.replace(/\/+$/, '')

const ensureLeadingSlash = (value: string): string => (value.startsWith('/') ? value : `/${value}`)

/**
 * Builds an absolute URL suitable for `window.location` navigation.
 *
 * Important: this must respect `VITE_API_URL` when the API is hosted on a
 * different origin than the SPA. Using hard-coded `/api/...` can cause the
 * browser to load the SPA instead of the backend (appears as a redirect to home).
 */
export const getApiBaseForNavigation = (): string => {
  const configured = String((api.defaults.baseURL ?? env.API_URL ?? '') || '').trim()

  if (!configured) {
    // Dev default: Vite proxy expects /api
    return `${window.location.origin}/api`
  }

  if (/^https?:\/\//i.test(configured)) {
    return stripTrailingSlashes(configured)
  }

  if (configured.startsWith('/')) {
    return stripTrailingSlashes(`${window.location.origin}${configured}`)
  }

  return stripTrailingSlashes(`${window.location.origin}/${configured}`)
}

export const buildApiNavigationUrl = (
  path: string,
  params?: Record<string, string | number | boolean | undefined | null>
): string => {
  const base = getApiBaseForNavigation()
  const normalizedPath = ensureLeadingSlash(path)

  const url = new URL(stripTrailingSlashes(base) + normalizedPath)

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue
      url.searchParams.set(key, String(value))
    }
  }

  return url.toString()
}
